import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';

interface DialogOptions {
  title?: string;
  type?: 'info' | 'warning' | 'success' | 'danger';
  defaultValue?: string;
  placeholder?: string;
}

interface DialogContextProps {
  showAlert: (message: string, options?: DialogOptions) => void;
  showConfirm: (message: string, options?: DialogOptions) => Promise<boolean>;
  showPrompt: (message: string, options?: DialogOptions) => Promise<string | null>;
}

const DialogContext = createContext<DialogContextProps | undefined>(undefined);

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};

interface DialogState {
  isOpen: boolean;
  message: string;
  title: string;
  type: 'info' | 'warning' | 'success' | 'danger';
  isConfirm: boolean;
  isPrompt: boolean;
  promptValue: string;
  promptPlaceholder?: string;
  resolve?: (value: boolean) => void;
  resolvePrompt?: (value: string | null) => void;
}

export const DialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<DialogState>({
    isOpen: false,
    message: '',
    title: '',
    type: 'info',
    isConfirm: false,
    isPrompt: false,
    promptValue: '',
  });

  const showAlert = useCallback((message: string, options?: DialogOptions) => {
    setState({
      isOpen: true,
      message,
      title: options?.title || 'Informasi',
      type: options?.type || 'info',
      isConfirm: false,
      isPrompt: false,
      promptValue: '',
    });
  }, []);

  const showConfirm = useCallback((message: string, options?: DialogOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({
        isOpen: true,
        message,
        title: options?.title || 'Konfirmasi',
        type: options?.type || 'warning',
        isConfirm: true,
        isPrompt: false,
        promptValue: '',
        resolve,
      });
    });
  }, []);

  const showPrompt = useCallback((message: string, options?: DialogOptions): Promise<string | null> => {
    return new Promise((resolve) => {
      setState({
        isOpen: true,
        message,
        title: options?.title || 'Input Data',
        type: options?.type || 'info',
        isConfirm: false,
        isPrompt: true,
        promptValue: options?.defaultValue || '',
        promptPlaceholder: options?.placeholder || '',
        resolvePrompt: resolve,
      });
    });
  }, []);

  const handleClose = (result: boolean) => {
    setState((prev) => ({ ...prev, isOpen: false }));
    if (state.isPrompt) {
      if (state.resolvePrompt) {
        state.resolvePrompt(result ? state.promptValue : null);
      }
    } else {
      if (state.resolve) {
        state.resolve(result);
      }
    }
  };

  const getIcon = () => {
    switch (state.type) {
      case 'danger':
      case 'warning':
        return <AlertCircle className={`w-8 h-8 ${state.type === 'danger' ? 'text-rose-500' : 'text-amber-500'}`} />;
      case 'success':
        return <CheckCircle2 className="w-8 h-8 text-emerald-500" />;
      default:
        return <Info className="w-8 h-8 text-sky-500" />;
    }
  };

  return (
    <DialogContext.Provider value={{ showAlert, showConfirm, showPrompt }}>
      {children}

      {state.isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div 
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" 
            onClick={() => !state.isConfirm && !state.isPrompt && handleClose(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="shrink-0 mt-1">{getIcon()}</div>
                <div className="flex-1 pt-1.5">
                  <h3 className="text-lg font-bold text-slate-800 leading-none mb-2">
                    {state.title}
                  </h3>
                  <p className={`text-sm text-slate-600 leading-relaxed ${state.isPrompt ? 'mb-3' : ''}`}>
                    {state.message}
                  </p>
                  {state.isPrompt && (
                    <input
                      autoFocus
                      type="text"
                      value={state.promptValue}
                      onChange={(e) => setState(prev => ({ ...prev, promptValue: e.target.value }))}
                      placeholder={state.promptPlaceholder}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none transition text-sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleClose(true);
                        if (e.key === 'Escape') handleClose(false);
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="bg-slate-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-slate-100">
              {(state.isConfirm || state.isPrompt) && (
                <button
                  onClick={() => handleClose(false)}
                  className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 hover:text-slate-800 transition-colors"
                >
                  Batal
                </button>
              )}
              <button
                onClick={() => handleClose(true)}
                className={`px-4 py-2 text-sm font-semibold text-white rounded-lg transition-colors shadow-sm ${
                  state.type === 'danger' ? 'bg-rose-500 hover:bg-rose-600' :
                  state.type === 'warning' ? 'bg-amber-500 hover:bg-amber-600' :
                  state.type === 'success' ? 'bg-emerald-500 hover:bg-emerald-600' :
                  'bg-sky-600 hover:bg-sky-700'
                }`}
              >
                {(state.isConfirm || state.isPrompt) ? 'Ya, Lanjutkan' : 'Mengerti'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
};
